// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { OperatorConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Operator';
import FormDialog from '../../../components/Dialog/FormDialog';
import useModifyOperatorConfiguration from '../../../hooks/mutations/registry/configuration/useModifyOperatorConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import {
  PartyCredentialRequirementElement,
  toPartyCredentialRequirements,
  fromPartyCredentialRequirements,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';
import TemplateContract from '../../../utils/TemplateContract';
import { SUC_MOD_OP_CONF, ERR_MOD_OP_CONF } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  operatorConfiguration: TemplateContract<OperatorConfiguration>;
};

export const ModifyOperatorConfigurationDialog = ({
  open,
  onClose,
  operatorConfiguration,
}: DialogProps) => {
  const [providerCredentialRequirements, setProviderCredentialRequirements] = useState<
    PartyCredentialRequirementElement[]
  >([]);

  useEffect(() => {
    if (operatorConfiguration?.payload?.providerRequirements) {
      setProviderCredentialRequirements(
        fromPartyCredentialRequirements(operatorConfiguration.payload.providerRequirements),
      );
    }
  }, [operatorConfiguration]);

  const modifyOperatorConfiguration = useModifyOperatorConfiguration();

  const mutate = useMutate();

  const resetState = useCallback(() => {
    setProviderCredentialRequirements([]);
  }, [setProviderCredentialRequirements]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    const payload = {
      operatorConfigurationCid: operatorConfiguration.contractId,
      newProviderRequirements: toPartyCredentialRequirements(providerCredentialRequirements),
    };
    await mutate(modifyOperatorConfiguration, payload, SUC_MOD_OP_CONF, ERR_MOD_OP_CONF);
    handleClose();
  }, [
    providerCredentialRequirements,
    modifyOperatorConfiguration,
    operatorConfiguration.contractId,
    handleClose,
    mutate,
  ]);

  const disableSubmit = false;
  return (
    <FormDialog
      open={open}
      title="Modify Operator Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Modify"
      isSubmitDisabled={disableSubmit}
    >
      <Box mb={3}>
        <RequiredCredentialsInput
          list={providerCredentialRequirements}
          setList={setProviderCredentialRequirements}
          title="Required Credentials for Provider"
        />
      </Box>
    </FormDialog>
  );
};
